import { SiteUrls } from '@tableau/shared-urls';
import { PopoverPlacement } from '@tableau/widgets';
import React from 'react';
import { RemoteType } from '../../../../../gen-src/queries';
import {
  assertUnreachable,
  GraphLabel,
  LabelContent,
  LabelServiceAssetType,
  LabelSubjectArea,
  SiteUrlsContext,
  useRenderLabels,
  UseRenderLabelsParams,
  UseRenderLabelsResult,
} from '../../../data-catalog-shared';
import { ColumnDetailsDialogData } from '../ColumnDetails/types/ColumnDetailsDialogData';
import { DatabasesDisplayPlacePageData } from '../database/types/DatabasesDisplayPlacePageData';
import { TablesDisplayPlacePageData } from '../table/types/TablesDisplayPlacePageData';
import { VizClientDatasource } from '../vizclient/VizClientInterfaces';

type LabelUpstreamContentItemType = {
  id: string;
  name: string | null;
  __typename: string;
}

type LabelContentItem = DatabasesDisplayPlacePageData | TablesDisplayPlacePageData | ColumnDetailsDialogData | VizClientDatasource | LabelUpstreamContentItemType;

const gridInheritedTypes = ['datasource-fields', 'workbook-fields', 'flow-fields', 'vconn-columns', 'table-columns'];

export type LabelWrapperProps = {
  readonly category: 'warning' | 'certification' | 'sensitivity' | 'all';
  readonly itemInfo: LabelContentItem;
  readonly isEager?: boolean;
  readonly kind?: 'icon' | 'chip';
  readonly popoverPlacement?: PopoverPlacement;
  readonly wrapper?: (e: JSX.Element) => JSX.Element;
}

export function LabelWrapper(props: LabelWrapperProps): JSX.Element | null {
  const { certifications, warnings, sensitivities } = useRenderLabelsDC3(props);
  const wrapper = props.wrapper ?? ((e: JSX.Element) => e);

  const certificationElement = (certifications ? wrapper(certifications) : null);
  const warningElement = (warnings ? wrapper(warnings) : null);
  const sensitivityElement = (sensitivities ? wrapper(sensitivities) : null);

  switch (props.category) {
    case 'certification': return certificationElement;
    case 'warning': return warningElement;
    case 'sensitivity': return sensitivityElement;
    case 'all': return <>{certificationElement}{warningElement}{sensitivityElement}</>;
    default:
      assertUnreachable(props.category);
      return null;
  }
}

type UseRenderLabelsDC3Params = UseRenderLabelsParams & { readonly urls: SiteUrls }

function useRenderLabelsDC3({ itemInfo, kind, popoverPlacement }: LabelWrapperProps): UseRenderLabelsResult {
  const siteUrls = React.useContext(SiteUrlsContext);

  const contentInfo = getContentInfo(itemInfo, siteUrls);
  const isTable = itemInfo.__typename.includes('Table');
  const isEmbeddedDatasource = itemInfo.__typename === 'EmbeddedDatasource';

  const labelParams: UseRenderLabelsDC3Params = {
    catalogEnabled: true,
    content: {
      contentId: itemInfo.id,
      contentLuid: contentInfo.contentLuid ?? undefined,
      contentType: contentInfo.contentType,
      contentName: itemInfo.name ?? undefined,
      contentUrl: contentInfo.contentUrl ?? undefined,
      subjectArea: contentInfo.subjectArea ?? undefined,
      remoteType: contentInfo.remoteType ?? undefined,
    },
    includeUpstream: true,
    includeParented: isTable,
    probablyOrEager: 'eager',
    testId: contentInfo.testId,
    controls: {
      kind: kind ?? (isEmbeddedDatasource ? 'icon' : 'chip'),
      siteUrls: siteUrls,
      popoverPlacement 
    },
    urls: siteUrls,
  };

  return useRenderLabels(labelParams);
}

type ContentInfo = {
  contentLuid: string | null;
  contentType: LabelServiceAssetType | null;
  contentUrl: string | null;
  subjectArea: LabelSubjectArea | null;
  testId: string;
  remoteType?: RemoteType;
}

function getContentInfo(labelContentItem: LabelContentItem, siteUrls: SiteUrls): ContentInfo {
  switch (labelContentItem.__typename) {
    case 'DatabaseTable':
      return {
        contentLuid: (labelContentItem as TablesDisplayPlacePageData).luid,
        contentType: 'table',
        contentUrl: siteUrls.catalogTableUrl(labelContentItem.id),
        subjectArea: labelContentItem.__typename,
        testId: 'TablePlacePageLabelsMasthead',
      };
    case 'Column': {
      const columnContentItem = labelContentItem as ColumnDetailsDialogData;
      return {
        contentLuid: columnContentItem.luid,
        contentType: 'column',
        contentUrl: columnContentItem.table ? siteUrls.catalogTableColumnUrl(columnContentItem.table.id, columnContentItem.id) : null,
        subjectArea: 'Column',
        testId: 'ColumnDetailsLabelDialog',
        remoteType: columnContentItem.remoteType
      };
    }
    case 'ColumnField': case 'DatasourceField': case 'CombinedField':
    case 'CalculatedField': case 'BinField': case 'HierarchyField':
    case 'CombinedSetField': case 'SetField': case 'Field':
      return {
        contentLuid: null,
        contentType: null,
        contentUrl: null,
        subjectArea: 'Field',
        testId: 'FieldDetailsLabelDialog',
      };
    case 'FlowColumnOutputField':
    return {
      contentLuid: null,
      contentType: null,
      contentUrl: null,
      subjectArea: 'FlowColumnOutputField',
      testId: 'FlowColumnFieldDetailsLabelDialog',
    };
  case 'FlowFieldOutputField':
    return {
      contentLuid: null,
      contentType: null,
      contentUrl: null,
      subjectArea: 'FlowFieldOutputField',
      testId: 'FlowFieldDetailsLabelDialog',
    };
    case 'View':
      return {
        contentLuid: null,
        contentType: null,
        contentUrl: null,
        subjectArea: labelContentItem.__typename,
        testId: 'DataDetailsLabelsChip',
      };
    case 'EmbeddedDatasource':
      return {
        contentLuid: null,
        contentType: null,
        contentUrl: null,
        subjectArea: labelContentItem.__typename,
        testId: 'DataDetailsLabelsIcon',
      };
    case 'CloudFile':
    case 'File':
    case 'WebDataConnector':
    case 'DatabaseServer':
      return {
        contentLuid: (labelContentItem as DatabasesDisplayPlacePageData).luid,
        contentType: 'database',
        contentUrl: siteUrls.catalogDatabaseUrl(labelContentItem.id),
        subjectArea: labelContentItem.__typename,
        testId: 'DatabasePlacePageLabelsMasthead',
      };
    default:
      assertUnreachable(labelContentItem.__typename);
      throw new Error('Unreachable');
  }
}


export type LabelGridWrapperProps = {
  readonly category: 'WARNING' | 'CERTIFICATION' | 'SENSITIVITY';
  readonly labels: ReadonlyArray<GraphLabel>;
  readonly urls: SiteUrls;
  readonly source: string;
  readonly content: LabelContent;
}

export function labelCategoryToString(category: LabelGridWrapperProps['category']): 'warning' | 'certification' | 'sensitivity' {
  switch (category) {
    case 'CERTIFICATION': return 'certification';
    case 'WARNING': return 'warning';
    case 'SENSITIVITY': return 'sensitivity';
    default: assertUnreachable(category); return 'warning';
  }
}

function buildProbablyOrEager(category: LabelGridWrapperProps['category'], value: string, highestSeverity: boolean, count: number): UseRenderLabelsParams['probablyOrEager'] {
  if (count <= 0) {
    return undefined;
  }

  switch (category) {
    case 'CERTIFICATION':
      return {
        certified: true,
      };
    case 'WARNING':
      return {
        warning: {
          value,
          severe: highestSeverity,
          count,
        },
      };
    case 'SENSITIVITY':
      return {
        sensitivity: {value, count},
      };
    default:
      assertUnreachable(category);
      return undefined;
  }
}

export function getLabelParamsForGrids({ category, labels, urls, source, content }: LabelGridWrapperProps): UseRenderLabelsParams {
  const categoryLabels = labels.filter((label: { category: string }) => label.category === category);
  const isActiveList = categoryLabels.filter((label: { isActive: boolean }) => label.isActive);
  const count = isActiveList.length;
  const highestSeverity = categoryLabels.some((label: { isActive: boolean, isElevated: boolean }) => label.isActive && label.isElevated);
  const labelValue = count > 0 ? isActiveList[0].value : category;

  const labelParams: UseRenderLabelsParams = {
    catalogEnabled: true,
    content: content,
    probablyOrEager: buildProbablyOrEager(category, labelValue, highestSeverity, count),
    includeParented: source === 'tables',
    includeUpstream: gridInheritedTypes.includes(source),
    testId: source + '-' + category.toLowerCase() + '-column',
    controls: {
      kind: category === 'SENSITIVITY' ? 'chip' : 'icon',
      siteUrls: urls,
    }
  };
  return labelParams;
}

